/**
 * Archived envelopes (C6; TMMAPP-0108; audit 06 § A4 / C6, v2 L2335–2356): "Archived envelopes",
 * "Kept for audit. Restoring one returns it to the virtual account with its history intact.", one
 * row per archived envelope — its name, "{VA} · balance $X" and an outlined Unarchive for a writer —
 * then Close. The list is fetched when the dialog opens (`includeArchived`); a restore notifies
 * "{name} restored to {VA}." and the page reloads its detail. A read-only dialog closes with a
 * contained Close (pattern 10).
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { envelopesApi, type Envelope } from '@/api/envelopes';
import { ErrorCodes, isApiError } from '@/api/errors';
import type { VirtualAccountDetail } from '@/api/virtualAccounts';
import { DialogSubtitle } from '@/components/DialogSubtitle';
import { ErrorState } from '@/components/ErrorState';
import { formatMoneyMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useDialogFullScreen } from '@/shell/useDialogFullScreen';
import { resolveTokens } from '@/theme/theme';
import { orderEnvelopes } from './envelopeOrder';

interface ArchivedEnvelopesDialogProps {
  virtualAccount: VirtualAccountDetail;
  canWrite: boolean;
  onClose: () => void;
  onRestored: (envelope: Envelope) => void | Promise<void>;
  onSubscriptionRequired: () => void;
}

export function ArchivedEnvelopesDialog({
  virtualAccount,
  canWrite,
  onClose,
  onRestored,
  onSubscriptionRequired,
}: ArchivedEnvelopesDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const fullScreen = useDialogFullScreen();
  const titleId = useId();
  const subtitleId = useId();
  const [rows, setRows] = useState<Envelope[] | null>(null);
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const all = await envelopesApi.listForVirtualAccount(virtualAccount.id, {
        includeArchived: true,
      });
      setRows(orderEnvelopes(all.filter((envelope) => envelope.archivedAt !== null)));
      setFailure(null);
    } catch (error) {
      setFailure({ error });
    }
  }, [virtualAccount.id]);

  // StrictMode double-invokes the mount effect; the list is fetched once per open.
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void load();
  }, [load]);

  async function restore(envelope: Envelope) {
    if (busyId !== null) return;
    setBusyId(envelope.id);
    try {
      await envelopesApi.unarchive(envelope.id);
      setRows((prev) => (prev ?? []).filter((row) => row.id !== envelope.id));
      await onRestored(envelope);
    } catch (error) {
      if (isApiError(error) && error.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        onClose();
        onSubscriptionRequired();
      } else {
        setFailure({ error });
      }
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      aria-labelledby={titleId}
      aria-describedby={subtitleId}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="sm"
      sx={{ '& .MuiDialog-paper': { maxWidth: fullScreen ? undefined : 520 } }}
    >
      <DialogTitle id={titleId}>{t('virtualAccount.archivedEnvelopes.title')}</DialogTitle>
      <DialogSubtitle id={subtitleId}>{t('virtualAccount.archivedEnvelopes.sub')}</DialogSubtitle>
      <DialogContent sx={{ px: 0, pb: 0 }}>
        {failure !== null && (
          <Box sx={{ px: 3, py: 1.5 }}>
            <ErrorState
              variant="section"
              error={failure.error}
              what={t('errors.what.virtualAccount')}
              onRetry={() => void load()}
            />
          </Box>
        )}
        {rows === null && failure === null && (
          <Box sx={{ px: 3, py: 1.5 }}>
            <Skeleton variant="rounded" height={56} />
          </Box>
        )}
        {rows !== null && (
          <Box sx={{ mt: 1.5, borderTop: `1px solid ${tokens.line}` }}>
            {rows.length === 0 && (
              <Typography sx={{ p: '14px 24px', fontSize: 13, color: tokens.text2 }}>
                {t('virtualAccount.archivedEnvelopes.empty')}
              </Typography>
            )}
            {rows.map((envelope) => (
              <Box
                key={envelope.id}
                data-archived-envelope={envelope.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: '14px 24px',
                  borderBottom: `1px solid ${tokens.line}`,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{envelope.name}</Typography>
                  <Typography sx={{ fontSize: 12, color: tokens.textLabel('surfaceRaised') }}>
                    {t('virtualAccount.archivedEnvelopes.meta', {
                      virtualAccount: virtualAccount.name,
                      amount:
                        envelope.balanceMinor === null
                          ? '—'
                          : formatMoneyMinor(
                              envelope.balanceMinor,
                              virtualAccount.currency,
                              locale,
                            ),
                    })}
                  </Typography>
                </Box>
                {canWrite && (
                  <Button
                    variant="outlined"
                    loading={busyId === envelope.id}
                    disabled={busyId !== null && busyId !== envelope.id}
                    aria-label={t('virtualAccount.archivedEnvelopes.unarchiveFor', {
                      name: envelope.name,
                    })}
                    onClick={() => void restore(envelope)}
                  >
                    {t('envelope.unarchive')}
                  </Button>
                )}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ pt: 1.5 }}>
        <Button variant="contained" onClick={onClose}>
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
